import { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { hideScrollBar } from "../../../commonStyles";
import EmojiPickerProps from "./EmojiPicker";

const GIFList = ({
  onGifSelected,
  searchValue,
}: EmojiPickerProps.GIFListProps) => {
  const gifListRef = useRef<HTMLDivElement>(null);
  const column1Ref = useRef<HTMLDivElement>(null);
  const column2Ref = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [metaData, setMetaData] = useState<EmojiPickerProps.MetaData>({
    offset: 0,
    total_count: 0,
    count: 0,
  });

  const appendImage = (src: string, onClick: () => void) => {
    const img = document.createElement("img");
    img.src = src;
    img.style.width = "100%";
    img.style.display = "block";
    img.style.borderRadius = "8px";
    img.style.cursor = "pointer";
    img.onclick = onClick;
    img.onload = () => {
      const columns1 = column1Ref.current;
      const columns2 = column2Ref.current;
      const shortest =
        (columns1?.offsetHeight ?? 0) > (columns2?.offsetHeight ?? 0)
          ? columns2
          : columns1;
      if (shortest) {
        shortest.appendChild(img);
      }
    };
  };

  const fetchGifList = useCallback(
    (offset: number) => {
      setIsLoading(true);
      const params = new URLSearchParams({
        api_key: process.env.REACT_APP_GIPHY_API_KEY || "",
        offset: offset.toString(),
        q: searchValue,
        limit: "20",
      });

      fetch(
        process.env.REACT_APP_GIPHY +
          `${
            searchValue ? "/gifs/search" : "/gifs/trending"
          }?${params.toString()}`
      )
        .then((response) => response.json())
        .then((data) => {
          if (offset === 0) {
            column1Ref.current && (column1Ref.current.innerHTML = "");
            column2Ref.current && (column2Ref.current.innerHTML = "");
          }
          data.data.forEach((gif: EmojiPickerProps.GIF) => {
            appendImage(gif.images.fixed_height_downsampled.url, () =>
              onGifSelected(gif)
            );
          });
          setMetaData(data.pagination);
          offset === 0 &&
            gifListRef.current?.scrollTo({ top: 0, behavior: "smooth" });
        })
        .catch((error) => {
          console.error(error);
        })
        .finally(() => {
          setIsLoading(false);
        });
    },
    [searchValue, gifListRef, onGifSelected]
  );

  useEffect(() => {
    fetchGifList(0);
  }, [searchValue, fetchGifList]);

  return (
    <Container
      ref={gifListRef}
      onWheel={() => {
        const gifListElement = gifListRef.current as HTMLDivElement;
        const isApiCall =
          !isLoading &&
          metaData.offset < metaData.total_count &&
          gifListElement.scrollHeight -
            gifListElement.clientHeight -
            gifListElement.scrollTop <
            100;

        if (isApiCall) {
          fetchGifList(metaData.offset + metaData.count);
        }
      }}
    >
      <Wrapper ref={column1Ref} />
      <Wrapper ref={column2Ref} />
    </Container>
  );
};

export default GIFList;

const Container = styled.div`
  display: flex;
  gap: 8px;
  overflow-y: auto;
  ${hideScrollBar}
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  height: fit-content;
  width: 100%;
`;
